import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, CheckCircle2, XCircle, AlertCircle, Wrench, FileText, Anchor, Shield } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import useCanonical from '../hooks/useCanonical';

const SITE_URL = 'https://validatedboat.fr';

export default function MethodeValidatedBoat() {
  useCanonical(`${SITE_URL}/methode-validatedboat`);

  const jsonld = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "ValidatedBoat",
    "url": SITE_URL,
    "areaServed": "France",
    "serviceType": "Conseil nautique indépendant",
    "description": "Conseiller nautique indépendant à La Rochelle. Analyse d'annonce, audit pré-achat, accompagnement acheteur et vendeur de bateaux d'occasion.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "La Rochelle",
      "addressRegion": "Charente-Maritime",
      "addressCountry": "FR"
    }
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b] text-[#dfddd6]" data-testid="page-methode">
      <Helmet>
        <title>La méthode ValidatedBoat | Conseiller nautique indépendant</title>
        <meta name="description" content="Ce que ValidatedBoat vérifie, ce qu'un mécanicien ou un expert maritime doit faire, les limites de chaque prestation. Notre méthode transparente pour vous accompagner." />
        <script type="application/ld+json">{JSON.stringify(jsonld)}</script>
      </Helmet>
      <Header />

      <section className="pt-24 pb-16 md:pt-32 md:pb-20 bg-gradient-to-b from-[#302f2c] to-[#1a1c1b]">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <Link to="/" className="inline-flex items-center gap-2 text-[#888680] hover:text-[#d9fb06] mb-6 text-sm">
            <ArrowLeft size={16} /> Retour à l&apos;accueil
          </Link>
          <p className="text-[#d9fb06] font-bold uppercase text-sm tracking-widest mb-4">La méthode</p>
          <h1 className="font-black text-4xl md:text-6xl leading-tight text-[#dfddd6] mb-4">
            La méthode <span className="text-[#d9fb06]">ValidatedBoat</span>
          </h1>
          <p className="text-lg text-[#888680] max-w-2xl">
            Un accompagnement transparent. Voici précisément ce que je fais, ce que je ne fais pas, et à qui m&apos;en remettre pour le reste.
          </p>
        </div>
      </section>

      {/* Ce que ValidatedBoat vérifie */}
      <section className="py-16 md:py-20 bg-[#1a1c1b]">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <div className="bg-[#302f2c] border-2 border-[#d9fb06]/40 rounded-2xl p-6 md:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#d9fb06]/20 flex items-center justify-center">
                <CheckCircle2 className="text-[#d9fb06]" size={22} />
              </div>
              <h2 className="font-black text-2xl md:text-3xl uppercase text-[#d9fb06]">Ce que ValidatedBoat vérifie</h2>
            </div>
            <ul className="space-y-3 text-[#dfddd6]">
              {[
                "Cohérence du prix demandé par rapport au marché et aux transactions comparables",
                "Historique connu du modèle, défauts récurrents, rappels constructeur",
                "Analyse documentaire : factures, entretiens, actes de vente antérieurs",
                "Cohérence entre les photos, la description et les caractéristiques annoncées",
                "Points d'alerte visibles : signes d'osmose, corrosion, gréement dormant, motorisation, gel-coat",
                "Questions techniques et juridiques à poser au vendeur avant tout déplacement",
                "Recommandation argumentée : aller voir, négocier, poursuivre l'audit, ou passer votre chemin",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="text-[#d9fb06] flex-shrink-0 mt-1" size={16} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Ce que je ne fais pas */}
      <section className="py-16 md:py-20 bg-[#302f2c]">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <div className="text-center mb-10">
            <h2 className="font-black text-2xl md:text-4xl uppercase text-[#d9fb06] mb-3">Ce qui nécessite un autre professionnel</h2>
            <p className="text-[#888680] max-w-2xl mx-auto">Je vous oriente vers le bon interlocuteur pour chaque situation.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <ProCard
              icon={<Wrench className="text-orange-400" size={22} />}
              title="Diagnostic mécanique"
              who="Mécanicien marine agréé"
              items={[
                "Compression, injection, refroidissement",
                "État du bloc, transmission, embase",
                "Test moteur en charge et endoscopie",
              ]}
            />
            <ProCard
              icon={<Shield className="text-blue-400" size={22} />}
              title="Expertise maritime officielle"
              who="Expert maritime professionnel"
              items={[
                "Rapport contradictoire opposable",
                "Évaluation structurelle certifiée",
                "Contentieux et litiges vente",
              ]}
            />
            <ProCard
              icon={<Anchor className="text-cyan-400" size={22} />}
              title="Sortie d'eau et carène"
              who="Chantier naval / expert plongeur"
              items={[
                "Sondage anti-osmose",
                "État du safran, arbre, presse-étoupe",
                "Anodes, peinture antifouling, quille",
              ]}
            />
            <ProCard
              icon={<FileText className="text-purple-400" size={22} />}
              title="Analyse documentaire pointue"
              who="ValidatedBoat (inclus)"
              items={[
                "Cohérence entre annonce et documents",
                "Actes, factures, entretiens",
                "Questions à poser au vendeur",
              ]}
              highlight
            />
          </div>
        </div>
      </section>

      {/* Ce qui relève de l'analyse documentaire */}
      <section className="py-16 md:py-20 bg-[#1a1c1b]">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <div className="bg-[#302f2c] border border-[#3f4816] rounded-2xl p-6 md:p-10">
            <div className="flex items-center gap-3 mb-6">
              <AlertCircle className="text-[#d9fb06]" size={22} />
              <h2 className="font-black text-2xl md:text-3xl uppercase text-[#d9fb06]">Les limites de mon travail</h2>
            </div>
            <div className="space-y-4 text-[#dfddd6] leading-relaxed">
              <p>
                Mon analyse d&apos;annonce repose sur des éléments <strong>documentaires et visuels</strong> : le texte de l&apos;annonce,
                les photos, les documents que vous me transmettez, l&apos;historique connu du modèle, les prix de marché.
              </p>
              <p>
                Elle vous aide à <strong>éviter un déplacement inutile</strong>, <strong>poser les bonnes questions</strong> et
                <strong> décider si un audit physique s&apos;impose</strong>. Elle <strong>ne remplace pas</strong> :
              </p>
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li>Une inspection physique complète du bateau (essai en mer inclus)</li>
                <li>Un diagnostic mécanique par mécanicien marine</li>
                <li>Une expertise maritime officielle (rapport opposable)</li>
                <li>Une sortie d&apos;eau pour vérifier la coque et l&apos;œuvre vive</li>
              </ul>
              <p>
                Je vous <strong>oriente activement</strong> vers ces professionnels dès que la situation le justifie, et je peux vous accompagner sur l&apos;audit sur place lorsque c&apos;est pertinent.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-[#302f2c]">
        <div className="max-w-3xl mx-auto px-5 md:px-10 text-center">
          <h2 className="font-black text-2xl md:text-4xl uppercase text-[#d9fb06] mb-4">Prêt(e) à en savoir plus sur un bateau&nbsp;?</h2>
          <p className="text-[#888680] mb-8">Envoyez-moi le lien de l&apos;annonce. Je vous dis ce que j&apos;en pense.</p>
          <Button asChild data-testid="methode-cta-btn"
            className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full px-8 py-6 text-base font-bold uppercase">
            <Link to="/analyse-annonce-bateau">Faire analyser une annonce</Link>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const ProCard = ({ icon, title, who, items, highlight }) => (
  <div className={`rounded-2xl p-6 border ${highlight ? 'bg-[#3f4816]/40 border-[#d9fb06]/40' : 'bg-[#1a1c1b] border-[#3f4816]'}`}>
    <div className="flex items-center gap-2 mb-2">{icon}<h3 className={`font-bold ${highlight ? 'text-[#d9fb06]' : 'text-[#dfddd6]'}`}>{title}</h3></div>
    <p className={`text-xs uppercase font-bold mb-3 ${highlight ? 'text-[#d9fb06]' : 'text-[#888680]'}`}>{who}</p>
    <ul className="space-y-1">
      {items.map((it, i) => <li key={i} className="text-sm text-[#dfddd6] flex items-start gap-2"><span className={highlight ? 'text-[#d9fb06]' : 'text-[#888680]'}>•</span><span>{it}</span></li>)}
    </ul>
  </div>
);
